import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { ParticipantId } from './ParticipantId';
import { RaceContestant } from './RaceContestant';
import { RankingCategory } from './RankingCategory';
import { RelayTeamContestant } from './RelayTeamContestant';
import type { BibHandleType } from './../enums/BibHandleType';
import type { EntityState } from './../enums/EntityState';
import type { Gender } from './../enums/Gender';
import type { TimeType } from './../enums/TimeType';
import { formatDateIso } from '@/utilities/dateutils';

export class RaceContestantDetails extends BaseObj {
    public ID: number;
    public __id: number;

    public Address1: string | null;
    public __address1: string | null;

    public Address2: string | null;
    public __address2: string | null;

    public AddressCountryISO2: string | null;
    public __addresscountryiso2: string | null;

    public readonly Age: number | null;

    public Anonymous: boolean;
    public __anonymous: boolean;

    public Bib: number | null;
    public __bib: number | null;

    public BibHandleType: BibHandleType;
    public __bibhandletype: BibHandleType;

    public Birthday: Date | null;
    public __birthday: Date | null;

    public CategoryID: number | null;
    public __categoryid: number | null;

    public CategoryName: string | null;
    public __categoryname: string | null;

    public Chip1: string | null;
    public __chip1: string | null;

    public Chip2: string | null;
    public __chip2: string | null;

    public City: string | null;
    public __city: string | null;

    public ClubID: number | null;
    public __clubid: number | null;

    public CompanyClub: string | null;
    public __companyclub: string | null;

    public ContestantID: number;
    public __contestantid: number;

    public ContestantStatusID: number;
    public __contestantstatusid: number;

    public Email: string | null;
    public __email: string | null;

    public EventAge: number | null;
    public __eventage: number | null;

    public EventID: number;
    public __eventid: number;

    public EventTeamId: number | null;
    public __eventteamid: number | null;

    public Firstname: string | null;
    public __firstname: string | null;

    public Gender: Gender | null;
    public __gender: Gender | null;

    public GPSDeviceID: string | null;
    public __gpsdeviceid: string | null;

    public GPSDeviceIMEI: string | null;
    public __gpsdeviceimei: string | null;

    public GroupID: number | null;
    public __groupid: number | null;

    public GroupName: string | null;
    public __groupname: string | null;

    public GuideName: string | null;
    public __guidename: string | null;

    public HideFromTracking: boolean;
    public __hidefromtracking: boolean;

    public IgnoreGPSInEQME: boolean;
    public __ignoregpsineqme: boolean;

    public readonly InvoiceNumber: number | null;

    public IsFavorite: boolean;
    public __isfavorite: boolean;

    public ISonenID: string | null;
    public __isonenid: string | null;

    public LastSeen: Date | null;

    public LastSeenSource: TimeType;

    public LastSeenText: string | null;

    public LicenceID: number | null;
    public __licenceid: number | null;

    public Memo: string | null;
    public __memo: string | null;

    public NationalityCountryID: number;
    public __nationalitycountryid: number;

    public NextOfKin: string | null;
    public __nextofkin: string | null;

    public NextOfKinPhone: string | null;
    public __nextofkinphone: string | null;

    public readonly OrderNumber: number | null;

    public ParticipantID: number;
    public __participantid: number;

    public ParticipantIDs: ParticipantId[];
    public __participantids: ParticipantId[];

    public PersonalNumber: string | null;
    public __personalnumber: string | null;

    public Phone: string | null;
    public __phone: string | null;

    public PostalNumber: string | null;
    public __postalnumber: string | null;

    public RaceContestants: RaceContestant[];
    public __racecontestants: RaceContestant[];

    public RaceId: number;
    public __raceid: number;

    public RaceName: string | null;
    public __racename: string | null;

    public RankingCategories: RankingCategory[];
    public __rankingcategories: RankingCategory[];

    public RealTeamNationalityID: number | null;
    public __realteamnationalityid: number | null;

    public RegistrarEmail: string | null;
    public __registraremail: string | null;

    public RegistrarFirstname: string | null;
    public __registrarfirstname: string | null;

    public RegistrarSurname: string | null;
    public __registrarsurname: string | null;

    public RelayTeamContestants: RelayTeamContestant[];
    public __relayteamcontestants: RelayTeamContestant[];

    public RelayTeamName: string | null;
    public __relayteamname: string | null;

    public Seeding: number | null;
    public __seeding: number | null;

    public SeedingGroup: number;
    public __seedinggroup: number;

    public StartingTime: Date | null;
    public __startingtime: Date | null;

    public StartnumberID: number | null;
    public __startnumberid: number | null;

    public Surname: string | null;
    public __surname: string | null;

    public TeamID: number | null;
    public __teamid: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Address1?: string | null;
        Address2?: string | null;
        AddressCountryISO2?: string | null;
        Age?: number | null;
        Anonymous?: boolean;
        Bib?: number | null;
        BibHandleType?: BibHandleType;
        Birthday?: Date | null;
        CategoryID?: number | null;
        CategoryName?: string | null;
        Chip1?: string | null;
        Chip2?: string | null;
        City?: string | null;
        ClubID?: number | null;
        CompanyClub?: string | null;
        ContestantID?: number;
        ContestantStatusID?: number;
        Email?: string | null;
        EntityState?: EntityState;
        EventAge?: number | null;
        EventID?: number;
        EventTeamId?: number | null;
        Firstname?: string | null;
        Gender?: Gender | null;
        GPSDeviceID?: string | null;
        GPSDeviceIMEI?: string | null;
        GroupID?: number | null;
        GroupName?: string | null;
        GuideName?: string | null;
        HideFromTracking?: boolean;
        IgnoreGPSInEQME?: boolean;
        InvoiceNumber?: number | null;
        IsFavorite?: boolean;
        ISonenID?: string | null;
        LastSeen?: Date | null;
        LastSeenSource?: TimeType;
        LastSeenText?: string | null;
        LicenceID?: number | null;
        Memo?: string | null;
        NationalityCountryID?: number;
        NextOfKin?: string | null;
        NextOfKinPhone?: string | null;
        OrderNumber?: number | null;
        ParticipantID?: number;
        ParticipantIDs?: ParticipantId[];
        PersonalNumber?: string | null;
        Phone?: string | null;
        PostalNumber?: string | null;
        RaceContestants?: RaceContestant[];
        RaceId?: number;
        RaceName?: string | null;
        RankingCategories?: RankingCategory[];
        RealTeamNationalityID?: number | null;
        RegistrarEmail?: string | null;
        RegistrarFirstname?: string | null;
        RegistrarSurname?: string | null;
        RelayTeamContestants?: RelayTeamContestant[];
        RelayTeamName?: string | null;
        Seeding?: number | null;
        SeedingGroup?: number;
        StartingTime?: Date | null;
        StartnumberID?: number | null;
        Surname?: string | null;
        TeamID?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<RaceContestantDetails>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('RaceContestantDetails tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Address1 = values.Address1 ?? null;
        this.__address1 = values.Address1 ?? null;
        Object.defineProperty(this, 'Address1', {
            get(): string | null {
                return this.__address1;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Address1', this.__address1, value);
                this.__address1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Address2 = values.Address2 ?? null;
        this.__address2 = values.Address2 ?? null;
        Object.defineProperty(this, 'Address2', {
            get(): string | null {
                return this.__address2;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Address2', this.__address2, value);
                this.__address2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AddressCountryISO2 = values.AddressCountryISO2 ?? null;
        this.__addresscountryiso2 = values.AddressCountryISO2 ?? null;
        Object.defineProperty(this, 'AddressCountryISO2', {
            get(): string | null {
                return this.__addresscountryiso2;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('AddressCountryISO2', this.__addresscountryiso2, value);
                this.__addresscountryiso2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Age = values.Age ?? null;

        this.Anonymous = values.Anonymous ?? false;
        this.__anonymous = values.Anonymous ?? false;
        Object.defineProperty(this, 'Anonymous', {
            get(): boolean {
                return this.__anonymous;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Anonymous', this.__anonymous, value);
                this.__anonymous = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Bib = values.Bib ?? null;
        this.__bib = values.Bib ?? null;
        Object.defineProperty(this, 'Bib', {
            get(): number | null {
                return this.__bib;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Bib', this.__bib, value);
                this.__bib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.BibHandleType = values.BibHandleType ?? 'None';
        this.__bibhandletype = values.BibHandleType ?? 'None';
        Object.defineProperty(this, 'BibHandleType', {
            get(): BibHandleType {
                return this.__bibhandletype;
            },
            set(value: BibHandleType) {
                this.__entityStateHandler.propertyChanged('BibHandleType', this.__bibhandletype, value);
                this.__bibhandletype = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Birthday = values.Birthday ?? null;
        this.__birthday = values.Birthday ?? null;
        Object.defineProperty(this, 'Birthday', {
            get(): Date | null {
                return this.__birthday;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('Birthday', this.__birthday, value);
                this.__birthday = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryID = values.CategoryID ?? null;
        this.__categoryid = values.CategoryID ?? null;
        Object.defineProperty(this, 'CategoryID', {
            get(): number | null {
                return this.__categoryid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('CategoryID', this.__categoryid, value);
                this.__categoryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryName = values.CategoryName ?? null;
        this.__categoryname = values.CategoryName ?? null;
        Object.defineProperty(this, 'CategoryName', {
            get(): string | null {
                return this.__categoryname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('CategoryName', this.__categoryname, value);
                this.__categoryname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Chip1 = values.Chip1 ?? null;
        this.__chip1 = values.Chip1 ?? null;
        Object.defineProperty(this, 'Chip1', {
            get(): string | null {
                return this.__chip1;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Chip1', this.__chip1, value);
                this.__chip1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Chip2 = values.Chip2 ?? null;
        this.__chip2 = values.Chip2 ?? null;
        Object.defineProperty(this, 'Chip2', {
            get(): string | null {
                return this.__chip2;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Chip2', this.__chip2, value);
                this.__chip2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.City = values.City ?? null;
        this.__city = values.City ?? null;
        Object.defineProperty(this, 'City', {
            get(): string | null {
                return this.__city;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('City', this.__city, value);
                this.__city = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ClubID = values.ClubID ?? null;
        this.__clubid = values.ClubID ?? null;
        Object.defineProperty(this, 'ClubID', {
            get(): number | null {
                return this.__clubid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ClubID', this.__clubid, value);
                this.__clubid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CompanyClub = values.CompanyClub ?? null;
        this.__companyclub = values.CompanyClub ?? null;
        Object.defineProperty(this, 'CompanyClub', {
            get(): string | null {
                return this.__companyclub;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('CompanyClub', this.__companyclub, value);
                this.__companyclub = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantID = values.ContestantID ?? 0;
        this.__contestantid = values.ContestantID ?? 0;
        Object.defineProperty(this, 'ContestantID', {
            get(): number {
                return this.__contestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ContestantID', this.__contestantid, value);
                this.__contestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantStatusID = values.ContestantStatusID ?? 0;
        this.__contestantstatusid = values.ContestantStatusID ?? 0;
        Object.defineProperty(this, 'ContestantStatusID', {
            get(): number {
                return this.__contestantstatusid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ContestantStatusID', this.__contestantstatusid, value);
                this.__contestantstatusid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Email = values.Email ?? null;
        this.__email = values.Email ?? null;
        Object.defineProperty(this, 'Email', {
            get(): string | null {
                return this.__email;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Email', this.__email, value);
                this.__email = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventAge = values.EventAge ?? null;
        this.__eventage = values.EventAge ?? null;
        Object.defineProperty(this, 'EventAge', {
            get(): number | null {
                return this.__eventage;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('EventAge', this.__eventage, value);
                this.__eventage = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventTeamId = values.EventTeamId ?? null;
        this.__eventteamid = values.EventTeamId ?? null;
        Object.defineProperty(this, 'EventTeamId', {
            get(): number | null {
                return this.__eventteamid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('EventTeamId', this.__eventteamid, value);
                this.__eventteamid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Firstname = values.Firstname ?? null;
        this.__firstname = values.Firstname ?? null;
        Object.defineProperty(this, 'Firstname', {
            get(): string | null {
                return this.__firstname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Firstname', this.__firstname, value);
                this.__firstname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Gender = values.Gender ?? 'm';
        this.__gender = values.Gender ?? 'm';
        Object.defineProperty(this, 'Gender', {
            get(): Gender | null {
                return this.__gender;
            },
            set(value: Gender | null) {
                this.__entityStateHandler.propertyChanged('Gender', this.__gender, value);
                this.__gender = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPSDeviceID = values.GPSDeviceID ?? null;
        this.__gpsdeviceid = values.GPSDeviceID ?? null;
        Object.defineProperty(this, 'GPSDeviceID', {
            get(): string | null {
                return this.__gpsdeviceid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('GPSDeviceID', this.__gpsdeviceid, value);
                this.__gpsdeviceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPSDeviceIMEI = values.GPSDeviceIMEI ?? null;
        this.__gpsdeviceimei = values.GPSDeviceIMEI ?? null;
        Object.defineProperty(this, 'GPSDeviceIMEI', {
            get(): string | null {
                return this.__gpsdeviceimei;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('GPSDeviceIMEI', this.__gpsdeviceimei, value);
                this.__gpsdeviceimei = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GroupID = values.GroupID ?? null;
        this.__groupid = values.GroupID ?? null;
        Object.defineProperty(this, 'GroupID', {
            get(): number | null {
                return this.__groupid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('GroupID', this.__groupid, value);
                this.__groupid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GroupName = values.GroupName ?? null;
        this.__groupname = values.GroupName ?? null;
        Object.defineProperty(this, 'GroupName', {
            get(): string | null {
                return this.__groupname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('GroupName', this.__groupname, value);
                this.__groupname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GuideName = values.GuideName ?? null;
        this.__guidename = values.GuideName ?? null;
        Object.defineProperty(this, 'GuideName', {
            get(): string | null {
                return this.__guidename;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('GuideName', this.__guidename, value);
                this.__guidename = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HideFromTracking = values.HideFromTracking ?? false;
        this.__hidefromtracking = values.HideFromTracking ?? false;
        Object.defineProperty(this, 'HideFromTracking', {
            get(): boolean {
                return this.__hidefromtracking;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('HideFromTracking', this.__hidefromtracking, value);
                this.__hidefromtracking = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IgnoreGPSInEQME = values.IgnoreGPSInEQME ?? false;
        this.__ignoregpsineqme = values.IgnoreGPSInEQME ?? false;
        Object.defineProperty(this, 'IgnoreGPSInEQME', {
            get(): boolean {
                return this.__ignoregpsineqme;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IgnoreGPSInEQME', this.__ignoregpsineqme, value);
                this.__ignoregpsineqme = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.InvoiceNumber = values.InvoiceNumber ?? null;

        this.IsFavorite = values.IsFavorite ?? false;
        this.__isfavorite = values.IsFavorite ?? false;
        Object.defineProperty(this, 'IsFavorite', {
            get(): boolean {
                return this.__isfavorite;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsFavorite', this.__isfavorite, value);
                this.__isfavorite = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ISonenID = values.ISonenID ?? null;
        this.__isonenid = values.ISonenID ?? null;
        Object.defineProperty(this, 'ISonenID', {
            get(): string | null {
                return this.__isonenid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ISonenID', this.__isonenid, value);
                this.__isonenid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LastSeen = values.LastSeen ?? null;
        this.LastSeenSource = values.LastSeenSource ?? 'None';
        this.LastSeenText = values.LastSeenText ?? null;
        this.LicenceID = values.LicenceID ?? null;
        this.__licenceid = values.LicenceID ?? null;
        Object.defineProperty(this, 'LicenceID', {
            get(): number | null {
                return this.__licenceid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('LicenceID', this.__licenceid, value);
                this.__licenceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Memo = values.Memo ?? null;
        this.__memo = values.Memo ?? null;
        Object.defineProperty(this, 'Memo', {
            get(): string | null {
                return this.__memo;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Memo', this.__memo, value);
                this.__memo = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NationalityCountryID = values.NationalityCountryID ?? 0;
        this.__nationalitycountryid = values.NationalityCountryID ?? 0;
        Object.defineProperty(this, 'NationalityCountryID', {
            get(): number {
                return this.__nationalitycountryid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('NationalityCountryID', this.__nationalitycountryid, value);
                this.__nationalitycountryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NextOfKin = values.NextOfKin ?? null;
        this.__nextofkin = values.NextOfKin ?? null;
        Object.defineProperty(this, 'NextOfKin', {
            get(): string | null {
                return this.__nextofkin;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('NextOfKin', this.__nextofkin, value);
                this.__nextofkin = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NextOfKinPhone = values.NextOfKinPhone ?? null;
        this.__nextofkinphone = values.NextOfKinPhone ?? null;
        Object.defineProperty(this, 'NextOfKinPhone', {
            get(): string | null {
                return this.__nextofkinphone;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('NextOfKinPhone', this.__nextofkinphone, value);
                this.__nextofkinphone = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OrderNumber = values.OrderNumber ?? null;

        this.ParticipantID = values.ParticipantID ?? 0;
        this.__participantid = values.ParticipantID ?? 0;
        Object.defineProperty(this, 'ParticipantID', {
            get(): number {
                return this.__participantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ParticipantID', this.__participantid, value);
                this.__participantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ParticipantIDs = values.ParticipantIDs ?? [];
        this.__participantids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.ParticipantIDs ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', newVal),
        });
        this.__participantids.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs),
            );
        });
        Object.defineProperty(this, 'ParticipantIDs', {
            get(): ParticipantId[] {
                return this.__participantids;
            },
            set(value: ParticipantId[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__participantids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: ParticipantId[], newVal: ParticipantId[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', newVal),
                });
                this.__participantids.forEach((child: ParticipantId) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs);
            },
            enumerable: true,
            configurable: true,
        });

        this.PersonalNumber = values.PersonalNumber ?? null;
        this.__personalnumber = values.PersonalNumber ?? null;
        Object.defineProperty(this, 'PersonalNumber', {
            get(): string | null {
                return this.__personalnumber;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('PersonalNumber', this.__personalnumber, value);
                this.__personalnumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Phone = values.Phone ?? null;
        this.__phone = values.Phone ?? null;
        Object.defineProperty(this, 'Phone', {
            get(): string | null {
                return this.__phone;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Phone', this.__phone, value);
                this.__phone = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PostalNumber = values.PostalNumber ?? null;
        this.__postalnumber = values.PostalNumber ?? null;
        Object.defineProperty(this, 'PostalNumber', {
            get(): string | null {
                return this.__postalnumber;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('PostalNumber', this.__postalnumber, value);
                this.__postalnumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestants = values.RaceContestants ?? [];
        this.__racecontestants = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.RaceContestants ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', newVal),
        });
        this.__racecontestants.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', this.RaceContestants),
            );
        });
        Object.defineProperty(this, 'RaceContestants', {
            get(): RaceContestant[] {
                return this.__racecontestants;
            },
            set(value: RaceContestant[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__racecontestants = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: RaceContestant[], newVal: RaceContestant[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', newVal),
                });
                this.__racecontestants.forEach((child: RaceContestant) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', this.RaceContestants),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', this.RaceContestants);
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceId = values.RaceId ?? 0;
        this.__raceid = values.RaceId ?? 0;
        Object.defineProperty(this, 'RaceId', {
            get(): number {
                return this.__raceid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceId', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceName = values.RaceName ?? null;
        this.__racename = values.RaceName ?? null;
        Object.defineProperty(this, 'RaceName', {
            get(): string | null {
                return this.__racename;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('RaceName', this.__racename, value);
                this.__racename = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RankingCategories = values.RankingCategories ?? [];
        this.__rankingcategories = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.RankingCategories ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', newVal),
        });
        this.__rankingcategories.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', this.RankingCategories),
            );
        });
        Object.defineProperty(this, 'RankingCategories', {
            get(): RankingCategory[] {
                return this.__rankingcategories;
            },
            set(value: RankingCategory[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__rankingcategories = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: RankingCategory[], newVal: RankingCategory[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', newVal),
                });
                this.__rankingcategories.forEach((child: RankingCategory) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', this.RankingCategories),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RankingCategories', this.RankingCategories);
            },
            enumerable: true,
            configurable: true,
        });

        this.RealTeamNationalityID = values.RealTeamNationalityID ?? null;
        this.__realteamnationalityid = values.RealTeamNationalityID ?? null;
        Object.defineProperty(this, 'RealTeamNationalityID', {
            get(): number | null {
                return this.__realteamnationalityid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('RealTeamNationalityID', this.__realteamnationalityid, value);
                this.__realteamnationalityid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RegistrarEmail = values.RegistrarEmail ?? null;
        this.__registraremail = values.RegistrarEmail ?? null;
        Object.defineProperty(this, 'RegistrarEmail', {
            get(): string | null {
                return this.__registraremail;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('RegistrarEmail', this.__registraremail, value);
                this.__registraremail = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RegistrarFirstname = values.RegistrarFirstname ?? null;
        this.__registrarfirstname = values.RegistrarFirstname ?? null;
        Object.defineProperty(this, 'RegistrarFirstname', {
            get(): string | null {
                return this.__registrarfirstname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('RegistrarFirstname', this.__registrarfirstname, value);
                this.__registrarfirstname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RegistrarSurname = values.RegistrarSurname ?? null;
        this.__registrarsurname = values.RegistrarSurname ?? null;
        Object.defineProperty(this, 'RegistrarSurname', {
            get(): string | null {
                return this.__registrarsurname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('RegistrarSurname', this.__registrarsurname, value);
                this.__registrarsurname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayTeamContestants = values.RelayTeamContestants ?? [];
        this.__relayteamcontestants = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.RelayTeamContestants ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('RelayTeamContestants', newVal),
        });
        this.__relayteamcontestants.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RelayTeamContestants', this.RelayTeamContestants),
            );
        });
        Object.defineProperty(this, 'RelayTeamContestants', {
            get(): RelayTeamContestant[] {
                return this.__relayteamcontestants;
            },
            set(value: RelayTeamContestant[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__relayteamcontestants = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: RelayTeamContestant[], newVal: RelayTeamContestant[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RelayTeamContestants', newVal),
                });
                this.__relayteamcontestants.forEach((child: RelayTeamContestant) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RelayTeamContestants', this.RelayTeamContestants),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RelayTeamContestants', this.RelayTeamContestants);
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayTeamName = values.RelayTeamName ?? null;
        this.__relayteamname = values.RelayTeamName ?? null;
        Object.defineProperty(this, 'RelayTeamName', {
            get(): string | null {
                return this.__relayteamname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('RelayTeamName', this.__relayteamname, value);
                this.__relayteamname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Seeding = values.Seeding ?? null;
        this.__seeding = values.Seeding ?? null;
        Object.defineProperty(this, 'Seeding', {
            get(): number | null {
                return this.__seeding;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Seeding', this.__seeding, value);
                this.__seeding = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SeedingGroup = values.SeedingGroup ?? 0;
        this.__seedinggroup = values.SeedingGroup ?? 0;
        Object.defineProperty(this, 'SeedingGroup', {
            get(): number {
                return this.__seedinggroup;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('SeedingGroup', this.__seedinggroup, value);
                this.__seedinggroup = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartingTime = values.StartingTime ?? null;
        this.__startingtime = values.StartingTime ?? null;
        Object.defineProperty(this, 'StartingTime', {
            get(): Date | null {
                return this.__startingtime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('StartingTime', this.__startingtime, value);
                this.__startingtime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartnumberID = values.StartnumberID ?? null;
        this.__startnumberid = values.StartnumberID ?? null;
        Object.defineProperty(this, 'StartnumberID', {
            get(): number | null {
                return this.__startnumberid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('StartnumberID', this.__startnumberid, value);
                this.__startnumberid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Surname = values.Surname ?? null;
        this.__surname = values.Surname ?? null;
        Object.defineProperty(this, 'Surname', {
            get(): string | null {
                return this.__surname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Surname', this.__surname, value);
                this.__surname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TeamID = values.TeamID ?? null;
        this.__teamid = values.TeamID ?? null;
        Object.defineProperty(this, 'TeamID', {
            get(): number | null {
                return this.__teamid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('TeamID', this.__teamid, value);
                this.__teamid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Address1": ${JSON.stringify(this.Address1)}` +
            ',' +
            `"Address2": ${JSON.stringify(this.Address2)}` +
            ',' +
            `"AddressCountryISO2": ${JSON.stringify(this.AddressCountryISO2)}` +
            ',' +
            `"Age": ${JSON.stringify(this.Age)}` +
            ',' +
            `"Anonymous": ${JSON.stringify(this.Anonymous)}` +
            ',' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"BibHandleType": ${this.BibHandleType == 'None' ? 0 : this.BibHandleType == 'MoveAll' ? 1 : this.BibHandleType == 'KeepChip' ? 2 : this.BibHandleType == 'KeepDevice' ? 3 : this.BibHandleType == 'KeepAll' ? 4 : 0}` +
            ',' +
            `"Birthday": ${this.Birthday ? '"' + formatDateIso(this.Birthday).substring(0, 10) + '"' : null}` +
            ',' +
            `"CategoryID": ${JSON.stringify(this.CategoryID)}` +
            ',' +
            `"CategoryName": ${JSON.stringify(this.CategoryName)}` +
            ',' +
            `"Chip1": ${JSON.stringify(this.Chip1)}` +
            ',' +
            `"Chip2": ${JSON.stringify(this.Chip2)}` +
            ',' +
            `"City": ${JSON.stringify(this.City)}` +
            ',' +
            `"ClubID": ${JSON.stringify(this.ClubID)}` +
            ',' +
            `"CompanyClub": ${JSON.stringify(this.CompanyClub)}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"ContestantStatusID": ${JSON.stringify(this.ContestantStatusID)}` +
            ',' +
            `"Email": ${JSON.stringify(this.Email)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventAge": ${JSON.stringify(this.EventAge)}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"EventTeamId": ${JSON.stringify(this.EventTeamId)}` +
            ',' +
            `"Firstname": ${JSON.stringify(this.Firstname)}` +
            ',' +
            `"Gender": ${this.Gender == null ? null : this.Gender == 'm' ? 0 : this.Gender == 'f' ? 1 : this.Gender == 'n' ? 2 : null}` +
            ',' +
            `"GPSDeviceID": ${JSON.stringify(this.GPSDeviceID)}` +
            ',' +
            `"GPSDeviceIMEI": ${JSON.stringify(this.GPSDeviceIMEI)}` +
            ',' +
            `"GroupID": ${JSON.stringify(this.GroupID)}` +
            ',' +
            `"GroupName": ${JSON.stringify(this.GroupName)}` +
            ',' +
            `"GuideName": ${JSON.stringify(this.GuideName)}` +
            ',' +
            `"HideFromTracking": ${JSON.stringify(this.HideFromTracking)}` +
            ',' +
            `"IgnoreGPSInEQME": ${JSON.stringify(this.IgnoreGPSInEQME)}` +
            ',' +
            `"InvoiceNumber": ${JSON.stringify(this.InvoiceNumber)}` +
            ',' +
            `"IsFavorite": ${JSON.stringify(this.IsFavorite)}` +
            ',' +
            `"ISonenID": ${JSON.stringify(this.ISonenID)}` +
            ',' +
            `"LastSeen": ${this.LastSeen ? '"' + formatDateIso(this.LastSeen) + '"' : null}` +
            ',' +
            `"LastSeenSource": ${this.LastSeenSource == 'None' ? 0 : this.LastSeenSource == 'RaceContestantTime' ? 1 : this.LastSeenSource == 'ContestantTime' ? 2 : this.LastSeenSource == 'DNF' ? 3 : this.LastSeenSource == 'DNS' ? 4 : this.LastSeenSource == 'DSQ' ? 5 : this.LastSeenSource == 'DNQ' ? 6 : this.LastSeenSource == 'LAP' ? 7 : this.LastSeenSource == 'Finished' ? 8 : this.LastSeenSource == 'DNC' ? 9 : this.LastSeenSource == 'REL' ? 10 : this.LastSeenSource == 'EXC' ? 11 : 0}` +
            ',' +
            `"LastSeenText": ${JSON.stringify(this.LastSeenText)}` +
            ',' +
            `"LicenceID": ${JSON.stringify(this.LicenceID)}` +
            ',' +
            `"Memo": ${JSON.stringify(this.Memo)}` +
            ',' +
            `"NationalityCountryID": ${JSON.stringify(this.NationalityCountryID)}` +
            ',' +
            `"NextOfKin": ${JSON.stringify(this.NextOfKin)}` +
            ',' +
            `"NextOfKinPhone": ${JSON.stringify(this.NextOfKinPhone)}` +
            ',' +
            `"OrderNumber": ${JSON.stringify(this.OrderNumber)}` +
            ',' +
            `"ParticipantID": ${JSON.stringify(this.ParticipantID)}` +
            ',' +
            `"ParticipantIDs": [ ${this.ParticipantIDs.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"PersonalNumber": ${JSON.stringify(this.PersonalNumber)}` +
            ',' +
            `"Phone": ${JSON.stringify(this.Phone)}` +
            ',' +
            `"PostalNumber": ${JSON.stringify(this.PostalNumber)}` +
            ',' +
            `"RaceContestants": [ ${this.RaceContestants.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"RaceId": ${JSON.stringify(this.RaceId)}` +
            ',' +
            `"RaceName": ${JSON.stringify(this.RaceName)}` +
            ',' +
            `"RankingCategories": [ ${this.RankingCategories.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"RealTeamNationalityID": ${JSON.stringify(this.RealTeamNationalityID)}` +
            ',' +
            `"RegistrarEmail": ${JSON.stringify(this.RegistrarEmail)}` +
            ',' +
            `"RegistrarFirstname": ${JSON.stringify(this.RegistrarFirstname)}` +
            ',' +
            `"RegistrarSurname": ${JSON.stringify(this.RegistrarSurname)}` +
            ',' +
            `"RelayTeamContestants": [ ${this.RelayTeamContestants.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"RelayTeamName": ${JSON.stringify(this.RelayTeamName)}` +
            ',' +
            `"Seeding": ${JSON.stringify(this.Seeding)}` +
            ',' +
            `"SeedingGroup": ${JSON.stringify(this.SeedingGroup)}` +
            ',' +
            `"StartingTime": ${this.StartingTime ? '"' + formatDateIso(this.StartingTime) + '"' : null}` +
            ',' +
            `"StartnumberID": ${JSON.stringify(this.StartnumberID)}` +
            ',' +
            `"Surname": ${JSON.stringify(this.Surname)}` +
            ',' +
            `"TeamID": ${JSON.stringify(this.TeamID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of RaceContestantDetails from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Address1?: string | null;
        Address2?: string | null;
        AddressCountryISO2?: string | null;
        Anonymous?: boolean;
        Bib?: number | null;
        BibHandleType?: BibHandleType;
        Birthday?: Date | null;
        CategoryID?: number | null;
        CategoryName?: string | null;
        Chip1?: string | null;
        Chip2?: string | null;
        City?: string | null;
        ClubID?: number | null;
        CompanyClub?: string | null;
        ContestantID?: number;
        ContestantStatusID?: number;
        Email?: string | null;
        EventAge?: number | null;
        EventID?: number;
        EventTeamId?: number | null;
        Firstname?: string | null;
        Gender?: Gender | null;
        GPSDeviceID?: string | null;
        GPSDeviceIMEI?: string | null;
        GroupID?: number | null;
        GroupName?: string | null;
        GuideName?: string | null;
        HideFromTracking?: boolean;
        IgnoreGPSInEQME?: boolean;
        IsFavorite?: boolean;
        ISonenID?: string | null;
        LastSeen?: Date | null;
        LastSeenSource?: TimeType;
        LastSeenText?: string | null;
        LicenceID?: number | null;
        Memo?: string | null;
        NationalityCountryID?: number;
        NextOfKin?: string | null;
        NextOfKinPhone?: string | null;
        ParticipantID?: number;
        ParticipantIDs?: ParticipantId[];
        PersonalNumber?: string | null;
        Phone?: string | null;
        PostalNumber?: string | null;
        RaceContestants?: RaceContestant[];
        RaceId?: number;
        RaceName?: string | null;
        RankingCategories?: RankingCategory[];
        RealTeamNationalityID?: number | null;
        RegistrarEmail?: string | null;
        RegistrarFirstname?: string | null;
        RegistrarSurname?: string | null;
        RelayTeamContestants?: RelayTeamContestant[];
        RelayTeamName?: string | null;
        Seeding?: number | null;
        SeedingGroup?: number;
        StartingTime?: Date | null;
        StartnumberID?: number | null;
        Surname?: string | null;
        TeamID?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): RaceContestantDetails {
        return new RaceContestantDetails(values);
    }

    public static _FromServerJson(json: any): RaceContestantDetails {
        return new RaceContestantDetails({
            ID: json.ID,
            Address1: json.Address1 ?? '',
            Address2: json.Address2 ?? '',
            AddressCountryISO2: json.AddressCountryISO2 ?? '',
            Age: json.Age ?? null,
            Anonymous: json.Anonymous,
            Bib: json.Bib ?? null,
            BibHandleType:
                json.BibHandleType == 0
                    ? 'None'
                    : json.BibHandleType == 1
                      ? 'MoveAll'
                      : json.BibHandleType == 2
                        ? 'KeepChip'
                        : json.BibHandleType == 3
                          ? 'KeepDevice'
                          : json.BibHandleType == 4
                            ? 'KeepAll'
                            : 'None',
            Birthday: json.Birthday ? (json.Birthday instanceof Date ? json.Birthday : new Date(json.Birthday + ' 00:00:00')) : null,
            CategoryID: json.CategoryID ?? null,
            CategoryName: json.CategoryName ?? '',
            Chip1: json.Chip1 ?? '',
            Chip2: json.Chip2 ?? '',
            City: json.City ?? '',
            ClubID: json.ClubID ?? null,
            CompanyClub: json.CompanyClub ?? '',
            ContestantID: json.ContestantID,
            ContestantStatusID: json.ContestantStatusID,
            Email: json.Email ?? '',
            EventAge: json.EventAge ?? null,
            EventID: json.EventID,
            EventTeamId: json.EventTeamId ?? null,
            Firstname: json.Firstname ?? '',
            Gender: json.Gender == null ? null : json.Gender == 0 ? 'm' : json.Gender == 1 ? 'f' : json.Gender == 2 ? 'n' : null,
            GPSDeviceID: json.GPSDeviceID ?? '',
            GPSDeviceIMEI: json.GPSDeviceIMEI ?? '',
            GroupID: json.GroupID ?? null,
            GroupName: json.GroupName ?? '',
            GuideName: json.GuideName ?? '',
            HideFromTracking: json.HideFromTracking,
            IgnoreGPSInEQME: json.IgnoreGPSInEQME,
            InvoiceNumber: json.InvoiceNumber ?? null,
            IsFavorite: json.IsFavorite,
            ISonenID: json.ISonenID ?? '',
            LastSeen: json.LastSeen ? (json.LastSeen instanceof Date ? json.LastSeen : new Date(json.LastSeen)) : null,
            LastSeenSource:
                json.LastSeenSource == 0
                    ? 'None'
                    : json.LastSeenSource == 1
                      ? 'RaceContestantTime'
                      : json.LastSeenSource == 2
                        ? 'ContestantTime'
                        : json.LastSeenSource == 3
                          ? 'DNF'
                          : json.LastSeenSource == 4
                            ? 'DNS'
                            : json.LastSeenSource == 5
                              ? 'DSQ'
                              : json.LastSeenSource == 6
                                ? 'DNQ'
                                : json.LastSeenSource == 7
                                  ? 'LAP'
                                  : json.LastSeenSource == 8
                                    ? 'Finished'
                                    : json.LastSeenSource == 9
                                      ? 'DNC'
                                      : json.LastSeenSource == 10
                                        ? 'REL'
                                        : json.LastSeenSource == 11
                                          ? 'EXC'
                                          : 'None',
            LastSeenText: json.LastSeenText ?? '',
            LicenceID: json.LicenceID ?? null,
            Memo: json.Memo ?? '',
            NationalityCountryID: json.NationalityCountryID,
            NextOfKin: json.NextOfKin ?? '',
            NextOfKinPhone: json.NextOfKinPhone ?? '',
            OrderNumber: json.OrderNumber ?? null,
            ParticipantID: json.ParticipantID,
            ParticipantIDs: json.ParticipantIDs?.map((x: any) => ParticipantId._FromServerJson(x)),
            PersonalNumber: json.PersonalNumber ?? '',
            Phone: json.Phone ?? '',
            PostalNumber: json.PostalNumber ?? '',
            RaceContestants: json.RaceContestants?.map((x: any) => RaceContestant._FromServerJson(x)),
            RaceId: json.RaceId,
            RaceName: json.RaceName ?? '',
            RankingCategories: json.RankingCategories?.map((x: any) => RankingCategory._FromServerJson(x)),
            RealTeamNationalityID: json.RealTeamNationalityID ?? null,
            RegistrarEmail: json.RegistrarEmail ?? '',
            RegistrarFirstname: json.RegistrarFirstname ?? '',
            RegistrarSurname: json.RegistrarSurname ?? '',
            RelayTeamContestants: json.RelayTeamContestants?.map((x: any) => RelayTeamContestant._FromServerJson(x)),
            RelayTeamName: json.RelayTeamName ?? '',
            Seeding: json.Seeding ?? null,
            SeedingGroup: json.SeedingGroup,
            StartingTime: json.StartingTime ? (json.StartingTime instanceof Date ? json.StartingTime : new Date(json.StartingTime)) : null,
            StartnumberID: json.StartnumberID ?? null,
            Surname: json.Surname ?? '',
            TeamID: json.TeamID ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of RaceContestantDetails from another instance of RaceContestantDetails.
     * @param other The RaceContestantDetails you want to clone
     * @returns A cloned RaceContestantDetails
     */
    public static FromOther(other: RaceContestantDetails): RaceContestantDetails {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return RaceContestantDetails._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return RaceContestantDetails.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Address1'
            | 'Address2'
            | 'AddressCountryISO2'
            | 'Age'
            | 'Anonymous'
            | 'Bib'
            | 'BibHandleType'
            | 'Birthday'
            | 'CategoryID'
            | 'CategoryName'
            | 'Chip1'
            | 'Chip2'
            | 'City'
            | 'ClubID'
            | 'CompanyClub'
            | 'ContestantID'
            | 'ContestantStatusID'
            | 'Email'
            | 'EventAge'
            | 'EventID'
            | 'EventTeamId'
            | 'Firstname'
            | 'Gender'
            | 'GPSDeviceID'
            | 'GPSDeviceIMEI'
            | 'GroupID'
            | 'GroupName'
            | 'GuideName'
            | 'HideFromTracking'
            | 'IgnoreGPSInEQME'
            | 'InvoiceNumber'
            | 'IsFavorite'
            | 'ISonenID'
            | 'LastSeen'
            | 'LastSeenSource'
            | 'LastSeenText'
            | 'LicenceID'
            | 'Memo'
            | 'NationalityCountryID'
            | 'NextOfKin'
            | 'NextOfKinPhone'
            | 'OrderNumber'
            | 'ParticipantID'
            | 'ParticipantIDs'
            | 'PersonalNumber'
            | 'Phone'
            | 'PostalNumber'
            | 'RaceContestants'
            | 'RaceId'
            | 'RaceName'
            | 'RankingCategories'
            | 'RealTeamNationalityID'
            | 'RegistrarEmail'
            | 'RegistrarFirstname'
            | 'RegistrarSurname'
            | 'RelayTeamContestants'
            | 'RelayTeamName'
            | 'Seeding'
            | 'SeedingGroup'
            | 'StartingTime'
            | 'StartnumberID'
            | 'Surname'
            | 'TeamID'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'RaceContestantDetails';
}
